// src/app/dealerStatement/hooks/useGridColumnPreferences.ts
import {
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridColumnVisibilityModel,
  GridDensity,
  GridInitialState,
  GridSortModel,
  useGridApiRef
} from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useCommonApi } from '../../../api';

export type SavedPrefs = {
  columnVisibilityModel?: GridColumnVisibilityModel;
  orderedFields?: string[];
  columnWidths?: Record<string, number>;
  density?: GridDensity;
  sortModel?: GridSortModel;
};

const parsePrefs = (raw?: string): SavedPrefs | undefined => {
  if (!raw) return undefined;
  try {
    return JSON.parse(raw) as SavedPrefs;
  } catch {
    return undefined;
  }
};

export const useGridColumnPreferences = (apiRef: ReturnType<typeof useGridApiRef>) => {
  // applied = prefs ze serveru již byly aplikovány na grid
  const applied = useRef(false);
  // isApplying = právě probíhá programatická aplikace prefs → blokuj ukládání
  const isApplying = useRef(false);
  // debounce timer
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { mutate: savePrefs } = useMutation(useCommonApi().updateDealerStatementsDataGrid());

  // Načtení uložených preferencí z user endpointu (statementsDataGrid je JSON string)
  const { data: user, isSuccess } = useQuery(useCommonApi().getUser());
  const savedPrefs = parsePrefs(user?.statementsDataGrid);

  // Aplikuj viditelnost, šířky sloupců a řazení po načtení (jen jednou)
  // Pořadí sloupců (orderedFields) se aplikuje přes initialState prop na DataGrid
  useEffect(() => {
    if (!savedPrefs || applied.current || !apiRef.current) return;
    applied.current = true;

    // Nastav flag – callbacky z DataGridu během tohoto bloku nesmí ukládat
    isApplying.current = true;

    const { columnVisibilityModel, columnWidths, density, sortModel } = savedPrefs;

    if (columnVisibilityModel) {
      apiRef.current.setColumnVisibilityModel(columnVisibilityModel);
    }

    if (columnWidths) {
      Object.entries(columnWidths).forEach(([field, width]) => {
        try {
          apiRef.current.setColumnWidth(field, width);
        } catch {
          // sloupec nemusí existovat
        }
      });
    }

    if (density) {
      apiRef.current.setDensity(density);
    }

    // Aplikuj uložené řazení pouze pokud URL sync ještě žádné řazení nenastavil
    if (sortModel && apiRef.current.getSortModel().length === 0) {
      apiRef.current.setSortModel(sortModel);
    }

    // Uvolni flag až v dalším tick – DataGrid může volat callbacky asynchronně
    setTimeout(() => {
      isApplying.current = false;
    }, 0);
  }, [savedPrefs, apiRef]);

  const getCurrentState = useCallback((): SavedPrefs => {
    const cols = apiRef.current.getAllColumns();
    return {
      orderedFields: cols.map((c) => c.field),
      columnWidths: Object.fromEntries(cols.map((c) => [c.field, c.computedWidth ?? c.width ?? 100])),
      columnVisibilityModel: apiRef.current.state.columns.columnVisibilityModel,
      density: apiRef.current.state.density,
      sortModel: apiRef.current.getSortModel()
    };
  }, [apiRef]);

  // Debounced save – sloučí rychlé po sobě jdoucí změny do jednoho requestu
  const debouncedSave = useCallback(
    (prefs: SavedPrefs) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        savePrefs(prefs);
      }, 600);
    },
    [savePrefs]
  );

  // Sleduj změnu density přes subscribeEvent
  useEffect(() => {
    if (!apiRef.current) return;
    return apiRef.current.subscribeEvent('densityChange', () => {
      if (isApplying.current) return;
      debouncedSave(getCurrentState());
    });
  }, [apiRef, debouncedSave, getCurrentState]);

  // Sleduj změnu řazení přes subscribeEvent
  useEffect(() => {
    if (!apiRef.current) return;
    return apiRef.current.subscribeEvent('sortModelChange', () => {
      if (isApplying.current) return;
      debouncedSave(getCurrentState());
    });
  }, [apiRef, debouncedSave, getCurrentState]);

  const onColumnVisibilityModelChange = useCallback(
    (_model: GridColumnVisibilityModel) => {
      if (isApplying.current) return;
      debouncedSave(getCurrentState());
    },
    [debouncedSave, getCurrentState]
  );

  const onColumnWidthChange = useCallback(
    (_params: GridColumnResizeParams) => {
      if (isApplying.current) return;
      debouncedSave(getCurrentState());
    },
    [debouncedSave, getCurrentState]
  );

  const onColumnOrderChange = useCallback(
    (_params: GridColumnOrderChangeParams) => {
      if (isApplying.current) return;
      debouncedSave(getCurrentState());
    },
    [debouncedSave, getCurrentState]
  );

  // initialState pro DataGrid – aplikuje uložené pořadí sloupců, density a řazení při prvním renderu
  const initialState: GridInitialState = {
    columns: {
      columnVisibilityModel: savedPrefs?.columnVisibilityModel,
      orderedFields: savedPrefs?.orderedFields
    },
    density: savedPrefs?.density,
    sorting: {
      sortModel: savedPrefs?.sortModel
    }
  };

  return {
    // isReady: true pokud jsme dostali odpověď z API (ať už s daty nebo null)
    isReady: isSuccess,
    initialState,
    onColumnVisibilityModelChange,
    onColumnWidthChange,
    onColumnOrderChange
  };
};
